import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  CircularProgress,
  Divider,
  Button,
  Alert,
  Chip,
  Grid,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import TokenIcon, { TokenIconSize } from './TokenIcon';
import { TokenInfo } from '../store/dexConfigSlice';
import { useDexService } from '../contexts/DexServiceContext';
import { ethers } from 'ethers';
import { LogLevel } from '../types/dex';
import { useDexConfigRedux } from '../hooks/useDexConfigRedux';

/**
 * 基础代币列表
 */
const BASE_TOKENS: TokenInfo[] = [
  {
    symbol: "BNB",
    name: "Binance Coin",
    address: "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee", // 使用特殊地址代表原生BNB
    decimals: 18,
    logoUrl: "https://cmp.dodoex.io/BWNSMMqu68bSFJsr9vsiCV09IonH3yuAkR9gQxaipBw/rs:fit:96:96:0/g:no/aHR0cHM6Ly9pbWFnZS1wcm94eS5kb2RvZXguaW8vZDhvazF4akd3bU9GUXUtMVdzNEVRLVBNRnJYMW9DLXlmYTc3OGJUUkx0US9hSFIwY0hNNkx5OWpaRzR0YldWa2FXRXVaRzlrYjJWNExtbHZMMEpPUWw5alpEUTVNakU0TXpJMkwwSk9RbDlqWkRRNU1qRTRNekkyTG5CdVp3LnBuZw.webp"
  },
  {
    symbol: "USDT",
    name: "Tether USD",
    address: "0x55d398326f99059fF775485246999027B3197955",
    decimals: 18,
    logoUrl: "https://cmp.dodoex.io/QOl7CvP_WSmTUOMthbxQsNKjDj0s5kSjup69dW_-UeA/rs:fit:96:96:0/g:no/aHR0cHM6Ly9pbWFnZS1wcm94eS5kb2RvZXguaW8vTjdQU3RXV2ZGaGNTVzM1QlE2TEJIaF9keVROUEJqa1RLTXRwTll5cEhSWS9hSFIwY0hNNkx5OWpaRzR0YldWa2FXRXVaRzlrYjJWNExtbHZMMlZ5WXpJd0wyeHZaMjlmWmpJNVlXRmpZamxoTVM1d2JtYy5wbmc.webp"
  },
  {
    symbol: "USDC",
    name: "USD Coin",
    address: "0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d",
    decimals: 18,
    logoUrl: "https://cmp.dodoex.io/sQ5dF3FkjjQUsmfqFFE5cKq-cthh4u0wUooBE5Epf-k/rs:fit:96:96:0/g:no/aHR0cHM6Ly9pbWFnZS1wcm94eS5kb2RvZXguaW8vTDlEVElLa2dONG5mRkNTSF9GMUdXU3JiZkJDa2JZRTkwbmFDS0dIWnRsby9hSFIwY0hNNkx5OWpaRzR0YldWa2FXRXVaRzlrYjJWNExtbHZMMWRGVkVoZmFXTnZibDh6TVRjd1pqTTBORGcxTDFkRlZFaGZhV052Ymw4ek1UY3daak0wTkRnMUxuQnVady5wbmc.webp"
  },
];

/**
 * TokenSelector组件属性接口
 */
interface TokenSelectorProps {
  /**
   * 对话框是否打开
   */
  open: boolean;
  /**
   * 关闭对话框的回调
   */
  onClose: () => void;
  /**
   * 选择代币的回调
   */
  onSelectToken: (token: TokenInfo) => void;
  /**
   * 对话框标题
   */
  title?: string;
  /**
   * 当前选中的代币
   */
  currentToken?: TokenInfo | null;
  /**
   * 排除的代币地址列表
   */
  excludeAddresses?: string[];
  /**
   * 自定义代币列表
   */
  customTokens?: TokenInfo[];
}

/**
 * 代币选择器组件
 * 用于选择交易代币
 */
const TokenSelector: React.FC<TokenSelectorProps> = ({
  open,
  onClose,
  onSelectToken,
  title = '选择代币',
  currentToken = null,
  excludeAddresses = [],
  customTokens = []
}) => {
  const { dexService, isInitialized, addLog } = useDexService();
  const { tokens, addCustomToken } = useDexConfigRedux();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filteredTokens, setFilteredTokens] = useState<TokenInfo[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingPancakeTokens, setIsLoadingPancakeTokens] = useState<boolean>(false);
  const [pancakeTokensLoaded, setPancakeTokensLoaded] = useState<boolean>(false);
  const [pancakeTokens, setPancakeTokens] = useState<TokenInfo[]>([]);
  const [allTokens, setAllTokens] = useState<TokenInfo[]>([]);
  
  // 自定义代币相关状态
  const [customTokenAddress, setCustomTokenAddress] = useState<string>('');
  const [isAddingToken, setIsAddingToken] = useState<boolean>(false);
  const [customTokenError, setCustomTokenError] = useState<string | null>(null);
  const [showAddTokenSection, setShowAddTokenSection] = useState<boolean>(false);

  // 控制对话框首次打开时加载PancakeSwap代币
  const hasLoadedPancakeTokens = useRef<boolean>(false);

  // 切换添加代币区域显示
  const toggleAddTokenSection = () => {
    setShowAddTokenSection(!showAddTokenSection);
    if (showAddTokenSection) {
      setCustomTokenAddress('');
      setCustomTokenError(null);
    }
  };

  // 加载PancakeSwap代币列表
  const loadPancakeSwapTokens = useCallback(async () => {
    if (isLoadingPancakeTokens) return;
    
    setIsLoadingPancakeTokens(true);
    try {
      addLog({
        level: LogLevel.INFO,
        message: '开始获取PancakeSwap代币列表'
      });
      
      const response = await fetch('https://tokens.pancakeswap.finance/pancakeswap-top-100.json');
      if (!response.ok) {
        throw new Error(`HTTP错误: ${response.status}`);
      }
      const data = await response.json();

      const tokens = data.tokens || [];
      
      // 转换为应用内的TokenInfo格式
      const formattedTokens: TokenInfo[] = tokens.map((token: any) => ({
        address: token.address,
        symbol: token.symbol,
        name: token.name,
        decimals: token.decimals,
        logoUrl: token.logoURI
      }));
      
      // 保存加载的代币并设置状态
      setPancakeTokens(formattedTokens);
      setPancakeTokensLoaded(true);
      
      addLog({
        level: LogLevel.SUCCESS,
        message: `成功加载 ${formattedTokens.length} 个PancakeSwap代币`
      });
    } catch (error) {
      console.error('获取PancakeSwap代币列表失败:', error);
      addLog({
        level: LogLevel.ERROR,
        message: '获取PancakeSwap代币列表失败'
      });
    } finally {
      setIsLoadingPancakeTokens(false);
    }
  }, [addLog, isLoadingPancakeTokens]);

  // 处理刷新PancakeSwap代币
  const handleRefreshPancakeSwapTokens = useCallback(() => {
    loadPancakeSwapTokens();
  }, [loadPancakeSwapTokens]);

  // 当对话框打开时尝试加载PancakeSwap代币
  useEffect(() => {
    if (open && !hasLoadedPancakeTokens.current && !pancakeTokensLoaded) {
      loadPancakeSwapTokens();
      hasLoadedPancakeTokens.current = true;
    }
  }, [open, pancakeTokensLoaded, loadPancakeSwapTokens]);

  // 初始化代币列表 - 合并所有来源的代币
  useEffect(() => {
    if (!open) return;

    setIsLoading(true);
    try {
      // 合并基础代币、Redux中的自定义代币、传入的自定义代币和PancakeSwap代币
      const mergedTokens = [
        ...BASE_TOKENS,
        ...(Array.isArray(tokens) ? tokens : []),
        ...customTokens,
        ...pancakeTokens
      ];
      
      // 去重
      const uniqueTokens = mergedTokens.filter((token, index, self) =>
        index === self.findIndex((t) => t.address.toLowerCase() === token.address.toLowerCase())
      );
      
      // 排除指定的代币
      const filteredList = uniqueTokens.filter(
        token => !excludeAddresses.includes(token.address.toLowerCase())
      );

      setAllTokens(filteredList);
      setFilteredTokens(filteredList);
    } catch (error) {
      console.error('初始化代币列表失败:', error);
      // 如果出错，至少显示基础代币
      setAllTokens(BASE_TOKENS);
      setFilteredTokens(BASE_TOKENS);
    } finally {
      setIsLoading(false);
    }
  }, [open, tokens, customTokens, excludeAddresses, pancakeTokens]);

  // 处理搜索
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setFilteredTokens(allTokens);
      return;
    }
    
    const lowerQuery = query.toLowerCase();
    const filtered = allTokens.filter(token => 
      token.name.toLowerCase().includes(lowerQuery) || 
      token.symbol.toLowerCase().includes(lowerQuery) || 
      token.address.toLowerCase().includes(lowerQuery)
    );
    
    setFilteredTokens(filtered);
  }, [allTokens]);

  // 处理代币选择
  const handleTokenSelect = (token: TokenInfo) => {
    onSelectToken(token);
    onClose();
  };

  // 处理搜索输入变更
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleSearch(event.target.value);
  };

  // 清除搜索
  const handleClearSearch = () => {
    setSearchQuery('');
    setFilteredTokens(allTokens);
  };
  
  // 处理自定义代币地址输入
  const handleCustomTokenAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCustomTokenAddress(event.target.value);
    setCustomTokenError(null);
  };
  
  // 添加自定义代币
  const handleAddCustomToken = useCallback(async () => {
    if (!customTokenAddress || !ethers.utils.isAddress(customTokenAddress)) {
      setCustomTokenError('请输入有效的代币合约地址');
      return;
    }

    if (!isInitialized || !dexService) {
      setCustomTokenError('DexService未初始化');
      return;
    }

    // 检查是否已存在该代币
    const tokenExists = [
      ...BASE_TOKENS, 
      ...(Array.isArray(tokens) ? tokens : []), 
      ...allTokens
    ].some(
      token => token.address.toLowerCase() === customTokenAddress.toLowerCase()
    );

    if (tokenExists) {
      setCustomTokenError('该代币已存在');
      return;
    }

    setIsAddingToken(true);
    setCustomTokenError(null);

    try {
      // 获取代币信息
      const tokenInfo = await dexService.getTokenInfo(customTokenAddress);
      
      if (!tokenInfo) {
        throw new Error('获取代币信息失败');
      }
      
      // 创建TokenInfo对象
      const newToken: TokenInfo = {
        address: customTokenAddress,
        symbol: tokenInfo.symbol,
        name: tokenInfo.name,
        decimals: tokenInfo.decimals
      };
      
      // 添加到Redux
      if (addCustomToken) {
        addCustomToken(newToken);
      }
      
      // 重置表单
      setCustomTokenAddress('');
      setShowAddTokenSection(false);
      
      // 记录日志
      addLog({
        level: LogLevel.SUCCESS,
        message: `成功添加自定义代币: ${newToken.symbol}`
      });
      
    } catch (error) {
      console.error('添加自定义代币失败:', error);
      setCustomTokenError('添加自定义代币失败');
      
      addLog({
        level: LogLevel.ERROR,
        message: '添加自定义代币失败'
      });
    } finally {
      setIsAddingToken(false);
    }
  }, [customTokenAddress, isInitialized, dexService, tokens, allTokens, addCustomToken, addLog]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: 'background.paper'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
      {title}
        <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 0, pb: 3 }}>
        {/* 搜索框 */}
        <TextField
          fullWidth
          autoFocus
          placeholder="搜索代币名称、符号或地址"
          value={searchQuery}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  edge="end"
                  aria-label="清除搜索"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
            sx: { borderRadius: 2, padding: '2px 4px' }
          }}
          sx={{ mb: 2 }}
        />
        
        {/* 快捷选择代币按钮 */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>常用代币</Typography>
          <Grid container spacing={1}>
            {BASE_TOKENS.map((token) => (
              <Grid item key={token.address}>
                <Tooltip title={token.name}>
                  <Chip 
                    icon={<TokenIcon symbol={token.symbol}
                    size={TokenIconSize.SMALL} showSymbol={false} />}
                    label={token.symbol}
                    onClick={() => handleTokenSelect(token)}
                    sx={{ borderRadius: 1.5 }}
                    variant="outlined"
                  />
                </Tooltip>
              </Grid>
            ))}
          </Grid>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          {/* PancakeSwap代币加载状态/按钮 */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isLoadingPancakeTokens ? (
              <Chip
                icon={<CircularProgress size={16} />}
                label="加载PancakeSwap代币..."
                variant="outlined"
                size="small"
              />
            ) : (
              <Tooltip title="刷新PancakeSwap代币列表">
                <Chip
                  icon={<RefreshIcon fontSize="small" />}
                  label={pancakeTokensLoaded ? "刷新PancakeSwap代币" : "加载PancakeSwap代币"}
                  onClick={handleRefreshPancakeSwapTokens}
                  variant="outlined"
                  size="small"
                  color="primary"
                />
              </Tooltip>
            )}
          </Box>
          
          {/* 添加自定义代币按钮 */}
          <Button
            startIcon={showAddTokenSection ? <CloseIcon /> : <AddIcon />}
            onClick={toggleAddTokenSection}
            color={showAddTokenSection ? "inherit" : "primary"}
            size="small"
            variant="text"
          >
            {showAddTokenSection ? '取消添加' : '添加自定义代币'}
          </Button>
        </Box>
        
        {/* 添加自定义代币部分 */}
        {showAddTokenSection && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 500 }}>
              添加自定义代币
            </Typography>
            
            <TextField
              fullWidth
              placeholder="输入代币合约地址"
              value={customTokenAddress}
              onChange={handleCustomTokenAddressChange}
              variant="outlined"
              size="small"
              sx={{ mb: 1.5 }}
              error={!!customTokenError}
              helperText={customTokenError}
              disabled={isAddingToken}
            />
            
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleAddCustomToken}
              disabled={isAddingToken || !customTokenAddress || !isInitialized}
            >
              {isAddingToken ? <CircularProgress size={24} /> : '添加代币'}
            </Button>
            
            {!isInitialized && (
              <Alert severity="warning" sx={{ mt: 1.5, fontSize: '0.75rem' }}>
                DexService未初始化，请先配置网络和DEX
              </Alert>
            )}
          </Box>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <List sx={{ pt: 0, maxHeight: '400px', overflow: 'auto' }}>
            {filteredTokens.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">未找到匹配的代币</Typography>
              </Box>
            ) : (
              filteredTokens.map((token) => (
                <ListItem
                  component="div"
                  key={token.address}
                  onClick={() => handleTokenSelect(token)}
                  sx={{
                    borderRadius: 1.5,
                    mb: 1,
                    py: 1.5,
                    bgcolor: currentToken?.address === token.address ? 'action.selected' : 'transparent',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    },
                    cursor: 'pointer'
                  }}
                >
                
                  <ListItemText
                    primary={token.symbol}
                    secondary={token.name}
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}
                    secondaryTypographyProps={{
                      variant: 'body2',
                      color: 'text.secondary',
                      fontSize: '0.85rem'
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {token.address.substring(0, 6)}...{token.address.substring(token.address.length - 4)}
                  </Typography>
                </ListItem>
              ))
            )}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TokenSelector;
